import { Card, IOption, Select } from '@ss/meta-core-components'
import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { LayerIcon } from '../../assets/icons/layerIcon'
import { config } from '../../config'
import useRest from '../../hooks/useRest'
import { IComparisonTrend, IGenericObject } from '@types'
import { Line } from '@components'
import { Serie } from '@nivo/line'

interface Props {
  searchField?: string | number
  filters: IGenericObject<IOption | null>
}

/**
 * Componente che mostra grafici temporali per la pagina start up comparison
 * @param props.searchField {string | number | undefined}
 * @param props.filters: {IGenericObject<IOption | null>}
 * @component
 */
export const TimeSeriesComparison: React.FC<Props> = (props) => {
  const { searchField, filters } = props

  const [sector, setSector] = useState<IOption>()
  const [chartsData, setChartsData] = useState<IComparisonTrend>()

  const { post: getChartData } = useRest<{ data: IComparisonTrend }>(
    '/comparison_trend'
  )

  //effect per popolare i grafici
  useEffect(() => {
    if (!sector || sector.value === '') return
    if (!filters.aggregation || !filters.field) return
    getChartData({
      index: config.dataSet.comparisonIndex,
      field: filters.field.value,
      aggregation: filters.aggregation.value,
      sezione: sector.value,
    }).then(({ data }) => {
      setChartsData({ ...data })
    })
  }, [sector, filters])

  const getTitle = () => {
    if (!searchField) return ''
    if (typeof searchField !== 'string') return `${searchField}`
    return searchField.replaceAll('_', ' ').toUpperCase()
  }

  return (
    <Card position={'start'} width={'100%'} height={'max-content'}>
      <Title>
        <Icon>
          <LayerIcon />
        </Icon>
        TIME SERIES - {getTitle()} - {filters.aggregation?.label?.toUpperCase()}
      </Title>
      <Container data-cy={`comparison-time-series-content`} charts={!!sector}>
        <FilterContainer data-cy={'comparison-filters'}>
          <InputContainer>
            <InputLabel>Sezione attivita'</InputLabel>
            <Select
              dataCy={'ateco'}
              options={config.filters.comparison_ateco}
              onChange={(options) => {
                setSector(options?.[0])
              }}
            />
          </InputContainer>
        </FilterContainer>
        {sector && sector.value !== '' ? (
          <Charts data-cy={'comparison-time-series-chart'}>
            <ChartContainer>
              <ChartTitle data-cy={'sector-chart-title'}>
                {sector && sector.label ? sector.label : ''}
              </ChartTitle>
              {/*chart per settore*/}
              <Line
                customColors={['#2C96D3', '#9F8FDD']}
                yFormat={'KGM'}
                data={chartsData?.sezione_attività || []}
                domain={{
                  max: chartsData?.max_sezione_attività || 0,
                  min: chartsData?.min_sezione_attività || 0,
                }}
                dataCy={'comparison-sector'}
                dimensions={{ height: '400px' }}
                margin={{ top: 20, right: 60, bottom: 60, left: 90 }}
              />
            </ChartContainer>
            <ChartContainer>
              <ChartTitle data-cy={'global-chart-title'}> GLOBALE </ChartTitle>
              {/*chart globale*/}
              <Line
                yFormat={'KGM'}
                data={chartsData?.global || []}
                axisLegend={{}}
                domain={{
                  max: chartsData?.max_global || 0,
                  min: chartsData?.min_global || 0,
                }}
                customColors={['#2C96D3', '#9F8FDD']}
                dataCy={'comparison-global'}
                dimensions={{ height: '400px' }}
                margin={{ top: 20, right: 60, bottom: 60, left: 90 }}
              />
            </ChartContainer>
          </Charts>
        ) : (
          <></>
        )}
      </Container>
    </Card>
  )
}

//region style
const Container = styled.div<{ charts: boolean }>`
  display: grid;
  ${({ charts }) =>
    charts ? 'grid-template-rows: 30px auto' : 'grid-template-rows: 30px'};
  row-gap: 20px;
`
const Title = styled.div`
  font-weight: 600;
  font-size: 16px;
  line-height: 19px;
  display: grid;
  grid-template-columns: 45px auto 50px;
  width: 100%;
  gap: 30px;
  padding-bottom: 20px;
`
const Icon = styled.div`
  place-self: center;
`
const FilterContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 3fr;
  gap: 20px;
`
const InputContainer = styled.div`
  display: grid;
  grid-template-columns: 150px auto;
  gap: 10px;
`
const InputLabel = styled.div`
  place-self: center;
`
const Charts = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
`
const ChartContainer = styled.div`
  display: grid;
  grid-template-rows: 30px auto;
`
const ChartTitle = styled.div`
  font-weight: 600;
  font-size: 16px;
  line-height: 19px;
  place-self: center;
`
//endregion
